import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { trackPageView } from './utils/analytics';
import ProtectedRoute from './components/ProtectedRoute';
import SubscriptionGuard from './components/SubscriptionGuard';
import CompanySetupGuard from './components/CompanySetupGuard';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Hierarchy from './pages/Hierarchy';
import Members from './pages/Members';
import Customers from './pages/Customers';
import CustomerDetails from './pages/CustomerDetails';
import AffiliatedCustomers from './pages/AffiliatedCustomers';
import AffiliatedCustomerDetails from './pages/AffiliatedCustomerDetails';
import Products from './pages/Products';
import Documents from './pages/Documents';
import AccessManagement from './pages/AccessManagement';
import PendingRequests from './pages/PendingRequests';
import LeadManagement from './pages/LeadManagement';
import LearningCenter from './pages/LearningCenter';
import Reports from './pages/Reports';
import AttendanceMarker from './pages/AttendanceMarker';
import MarkAttendance from './pages/MarkAttendance';
import AttendanceScanner from './pages/AttendanceScanner';
import InvoiceView from './pages/InvoiceView';
import HealthReportViewer from './pages/HealthReportViewer';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import AboutUs from './pages/AboutUs';
import MealPlanTemplates from './pages/MealPlanTemplates';
import MealPlanViewer from './pages/MealPlanViewer';
import PlatformInvoices from './pages/PlatformInvoices';
import AdminSubscriptionInvoices from './pages/AdminSubscriptionInvoices';
import TrainingVideos from './pages/TrainingVideos';
import PlatformTour from './pages/PlatformTour';
import ContestManagement from './pages/ContestManagement';
import ContestDetails from './pages/ContestDetails';
import Leaderboard from './pages/Leaderboard';
import InventoryManagement from './pages/InventoryManagement';
import BulkUpload from './pages/BulkUpload';
import StartJourney from './pages/StartJourney';
import BadgeManagement from './pages/BadgeManagement';
import { LanguageProvider } from './contexts/LanguageContext';
import './App.css';

function AppContent() {
  const location = useLocation();
  const isInvoiceView = location.pathname.startsWith('/invoices/');
  const isHealthReportView = location.pathname.startsWith('/hreport/');
  const isStartJourneyView = location.pathname.startsWith('/start-journey/');
  const isPublicStandaloneView = isInvoiceView || isHealthReportView || isStartJourneyView;

  // Track page views
  useEffect(() => {
    const pagePath = location.pathname;
    const pageTitle = document.title;
    trackPageView(pagePath, pageTitle);
  }, [location]);

  return (
    <div className="App">
      {!isPublicStandaloneView && <Navbar />}
      <main className={isPublicStandaloneView ? "" : "main-content"}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/invoices/:token" element={<InvoiceView />} />
          <Route path="/hreport/:uniqueIdentifier" element={<HealthReportViewer />} />
          <Route path="/meal-plan/:uniqueIdentifier" element={<MealPlanViewer />} />
          <Route path="/mark-attendance/:uuid" element={<MarkAttendance />} />
          <Route path="/start-journey/:code" element={<StartJourney />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/about-us" element={<AboutUs />} />
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <SubscriptionGuard>
                    <CompanySetupGuard>
                      <Dashboard />
                    </CompanySetupGuard>
                  </SubscriptionGuard>
                </ProtectedRoute>
              } />
              <Route path="/hierarchy" element={
                <ProtectedRoute>
                  <SubscriptionGuard>
                    <CompanySetupGuard>
                      <Hierarchy />
                    </CompanySetupGuard>
                  </SubscriptionGuard>
                </ProtectedRoute>
              } />
              <Route path="/members" element={
                <ProtectedRoute>
                  <SubscriptionGuard>
                    <CompanySetupGuard>
                      <Members />
                    </CompanySetupGuard>
                  </SubscriptionGuard>
                </ProtectedRoute>
              } />
              <Route path="/customers" element={
                <ProtectedRoute>
                  <SubscriptionGuard>
                    <CompanySetupGuard>
                      <Customers />
                    </CompanySetupGuard>
                  </SubscriptionGuard>
                </ProtectedRoute>
              } />
              <Route path="/customers/:customerId" element={
                <ProtectedRoute>
                  <SubscriptionGuard>
                    <CompanySetupGuard>
                      <CustomerDetails />
                    </CompanySetupGuard>
                  </SubscriptionGuard>
                </ProtectedRoute>
              } />
              <Route path="/affiliated-customers" element={
                <ProtectedRoute>
                  <SubscriptionGuard>
                    <CompanySetupGuard>
                      <AffiliatedCustomers />
                    </CompanySetupGuard>
                  </SubscriptionGuard>
                </ProtectedRoute>
              } />
              <Route path="/affiliated-customers/:customerId" element={
                <ProtectedRoute>
                  <SubscriptionGuard>
                    <CompanySetupGuard>
                      <AffiliatedCustomerDetails />
                    </CompanySetupGuard>
                  </SubscriptionGuard>
                </ProtectedRoute>
              } />
              <Route path="/products" element={
                <ProtectedRoute>
                  <SubscriptionGuard>
                    <CompanySetupGuard>
                      <Products />
                    </CompanySetupGuard>
                  </SubscriptionGuard>
                </ProtectedRoute>
              } />
              <Route path="/documents" element={
                <ProtectedRoute>
                  <SubscriptionGuard>
                    <CompanySetupGuard>
                      <Documents />
                    </CompanySetupGuard>
                  </SubscriptionGuard>
                </ProtectedRoute>
              } />
              <Route path="/badges" element={
                <ProtectedRoute><SubscriptionGuard><CompanySetupGuard><BadgeManagement /></CompanySetupGuard></SubscriptionGuard></ProtectedRoute>
              } />
              <Route path="/access-management" element={
                <ProtectedRoute>
                  <SubscriptionGuard>
                    <CompanySetupGuard>
                      <AccessManagement />
                    </CompanySetupGuard>
                  </SubscriptionGuard>
                </ProtectedRoute>
              } />
              <Route path="/pending-requests" element={
                <ProtectedRoute>
                  <SubscriptionGuard>
                    <CompanySetupGuard>
                      <PendingRequests />
                    </CompanySetupGuard>
                  </SubscriptionGuard>
                </ProtectedRoute>
              } />
              <Route path="/leads" element={
                <ProtectedRoute>
                  <SubscriptionGuard>
                    <CompanySetupGuard>
                      <LeadManagement />
                    </CompanySetupGuard>
                  </SubscriptionGuard>
                </ProtectedRoute>
              } />
              <Route path="/learning-center" element={
                <ProtectedRoute>
                  <SubscriptionGuard>
                    <CompanySetupGuard>
                      <LearningCenter />
                    </CompanySetupGuard>
                  </SubscriptionGuard>
                </ProtectedRoute>
              } />
              <Route path="/reports" element={
                <ProtectedRoute>
                  <SubscriptionGuard>
                    <CompanySetupGuard>
                      <Reports />
                    </CompanySetupGuard>
                  </SubscriptionGuard>
                </ProtectedRoute>
              } />
              <Route path="/attendance-marker" element={
                <ProtectedRoute>
                  <SubscriptionGuard>
                    <CompanySetupGuard>
                      <AttendanceMarker />
                    </CompanySetupGuard>
                  </SubscriptionGuard>
                </ProtectedRoute>
              } />
              <Route path="/meal-plan-templates" element={
                <ProtectedRoute>
                  <SubscriptionGuard>
                    <CompanySetupGuard>
                      <MealPlanTemplates />
                    </CompanySetupGuard>
                  </SubscriptionGuard>
                </ProtectedRoute>
              } />
              <Route path="/attendance/scan" element={
                <ProtectedRoute>
                  <SubscriptionGuard>
                    <CompanySetupGuard>
                      <AttendanceScanner />
                    </CompanySetupGuard>
                  </SubscriptionGuard>
                </ProtectedRoute>
              } />
              <Route path="/platform-invoices" element={
                <ProtectedRoute>
                  <SubscriptionGuard>
                    <CompanySetupGuard>
                      <PlatformInvoices />
                    </CompanySetupGuard>
                  </SubscriptionGuard>
                </ProtectedRoute>
              } />
              <Route path="/training-videos" element={
                <ProtectedRoute>
                  <SubscriptionGuard>
                    <CompanySetupGuard>
                      <TrainingVideos />
                    </CompanySetupGuard>
                  </SubscriptionGuard>
                </ProtectedRoute>
              } />
              <Route path="/contests" element={
                <ProtectedRoute>
                  <SubscriptionGuard>
                    <CompanySetupGuard>
                      <ContestManagement />
                    </CompanySetupGuard>
                  </SubscriptionGuard>
                </ProtectedRoute>
              } />
              <Route path="/contests/:contestId" element={
                <ProtectedRoute>
                  <SubscriptionGuard>
                    <CompanySetupGuard>
                      <ContestDetails />
                    </CompanySetupGuard>
                  </SubscriptionGuard>
                </ProtectedRoute>
              } />
              <Route path="/leaderboard" element={
                <ProtectedRoute>
                  <SubscriptionGuard>
                    <CompanySetupGuard>
                      <Leaderboard />
                    </CompanySetupGuard>
                  </SubscriptionGuard>
                </ProtectedRoute>
              } />
              <Route path="/inventory" element={
                <ProtectedRoute>
                  <SubscriptionGuard>
                    <CompanySetupGuard>
                      <InventoryManagement />
                    </CompanySetupGuard>
                  </SubscriptionGuard>
                </ProtectedRoute>
              } />
              <Route path="/bulk-upload" element={
                <ProtectedRoute>
                  <SubscriptionGuard>
                    <CompanySetupGuard>
                      <BulkUpload />
                    </CompanySetupGuard>
                  </SubscriptionGuard>
                </ProtectedRoute>
              } />
              <Route path="/platform-tour" element={
                <ProtectedRoute>
                  <SubscriptionGuard>
                    <CompanySetupGuard>
                      <PlatformTour />
                    </CompanySetupGuard>
                  </SubscriptionGuard>
                </ProtectedRoute>
              } />
              <Route path="/admin/subscription-invoices" element={
                <ProtectedRoute>
                  <SubscriptionGuard>
                    <CompanySetupGuard>
                      <AdminSubscriptionInvoices />
                    </CompanySetupGuard>
                  </SubscriptionGuard>
                </ProtectedRoute>
              } />
              <Route path="/attendance-marker" element={
                <ProtectedRoute>
                  <SubscriptionGuard>
                    <CompanySetupGuard>
                      <AttendanceMarker />
                    </CompanySetupGuard>
                  </SubscriptionGuard>
                </ProtectedRoute>
              } />
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </main>
      {!isPublicStandaloneView && <Footer />}
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <LanguageProvider>
        <Router>
          <AppContent />
        </Router>
      </LanguageProvider>
    </AuthProvider>
  );
}

export default App;
